import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { InventoryFilters, InventoryTable, AdjustmentForm, InventoryDetailsModal } from './components';
import { AsyncBoundary } from '../../../components/AsyncBoundary';
import {
  InventoryItem,
  AdjustmentForm as AdjustmentFormData,
  reasonToCode,
  isOutboundAdjustment,
} from './warehouseInventoryTypes';
import { useSkusStore } from '../../../store/skusStore';
import { useCategoriesStore } from '../../../store/categoriesStore';
import { Sku } from '../../../services/skusApi';
import { getInventorySummary, adjustLot, InventorySummaryRow } from '../../../services/inventoryApi';
import styles from './WarehouseInventory.module.css';

function adapt(s: Sku, summary?: InventorySummaryRow): InventoryItem {
  return {
    id: String(s.id),
    sku: s.sku_code,
    barcode: s.barcode,
    category: s.category_name || '—',
    totalQuantity: summary ? summary.on_hand : s.total_stock,
    reserved: summary ? summary.reserved : 0,
    available: summary ? summary.available : s.total_stock,
    location: summary && summary.locations.length ? summary.locations.join(', ') : '—',
  };
}

const WarehouseInventory: React.FC = () => {
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('All Categories');
  const [location, setLocation] = useState('All Location');
  const [summaryBySku, setSummaryBySku] = useState<Map<number, InventorySummaryRow>>(new Map());
  const [detailItem, setDetailItem] = useState<InventoryItem | null>(null);

  const skus = useSkusStore((s) => s.items);
  const loading = useSkusStore((s) => s.loading);
  const error = useSkusStore((s) => s.error);
  const fetchSkus = useSkusStore((s) => s.fetchList);
  const categories = useCategoriesStore((s) => s.items);
  const fetchCategories = useCategoriesStore((s) => s.fetchList);

  const loadSummary = useCallback(async () => {
    try {
      const rows = await getInventorySummary();
      setSummaryBySku(new Map(rows.map((r) => [r.sku_id, r])));
    } catch {
      // Non-fatal: fall back to skus.total_stock for available.
      setSummaryBySku(new Map());
    }
  }, []);

  useEffect(() => {
    fetchSkus();
    fetchCategories();
    loadSummary();
  }, [fetchSkus, fetchCategories, loadSummary]);

  const adapted = useMemo<InventoryItem[]>(
    () => skus.map((s) => adapt(s, summaryBySku.get(s.id))),
    [skus, summaryBySku]
  );

  const filteredItems = useMemo(() => {
    return adapted.filter((item) => {
      const q = search.toLowerCase();
      const matchesSearch = !search || item.sku.toLowerCase().includes(q) || (item.barcode || '').toLowerCase().includes(q);
      const matchesCategory = category === 'All Categories' || item.category === category;
      const matchesLocation = location === 'All Location' || item.location.split(', ').includes(location);
      return matchesSearch && matchesCategory && matchesLocation;
    });
  }, [adapted, search, category, location]);

  const categoryOptions = useMemo(
    () => ['All Categories', ...categories.map((c) => c.name)],
    [categories]
  );

  // Location filter options come from the real lot locations.
  const locationOptions = useMemo(() => {
    const set = new Set<string>();
    summaryBySku.forEach((row) => row.locations.forEach((l) => l && set.add(l)));
    return ['All Location', ...Array.from(set).sort()];
  }, [summaryBySku]);

  const handleViewDetails = (item: InventoryItem) => setDetailItem(item);

  // Apply a stock adjustment against the chosen lot, then refresh.
  const handleConfirmAdjustment = async (form: AdjustmentFormData) => {
    const lotId = Number(form.lotId);
    const qtyAbs = Math.abs(Number(form.quantity) || 0);
    if (!lotId || !qtyAbs) return;
    const sign = isOutboundAdjustment(form.adjustmentType, form.reason) ? -1 : 1;
    const reasonCode = reasonToCode[form.reason] ?? 'CORRECTION';
    await adjustLot(lotId, sign * qtyAbs, reasonCode);
    await Promise.all([fetchSkus(), loadSummary()]);
  };

  const handleCancelAdjustment = () => undefined;

  return (
    <div className={styles.container}>
      <div className={styles.tableCard}>
        <InventoryFilters
          searchValue={search}
          category={category}
          location={location}
          onSearchChange={setSearch}
          onCategoryChange={setCategory}
          onLocationChange={setLocation}
          categoryOptions={categoryOptions}
          locationOptions={locationOptions}
        />
        <AsyncBoundary
          loading={loading && skus.length === 0}
          error={error}
          empty={!loading && filteredItems.length === 0}
          emptyMessage="No inventory matches the current filters"
          onRetry={() => fetchSkus()}
        >
          <InventoryTable items={filteredItems} onViewDetails={handleViewDetails} />
        </AsyncBoundary>
      </div>

      <AdjustmentForm onConfirm={handleConfirmAdjustment} onCancel={handleCancelAdjustment} />

      <InventoryDetailsModal item={detailItem} onClose={() => setDetailItem(null)} />
    </div>
  );
};

export const WarehouseInventoryPage: React.FC = () => <WarehouseInventory />;
export default WarehouseInventory;
